import { Flex, Text, Button, Card, Heading, Box } from '@radix-ui/themes';
import React, { useState, useEffect } from 'react';
import { BiSend } from 'react-icons/bi';
import axiosInstance from '../config/axiosConfig';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatApiDateTime, getApiDateTimeMillis } from '../utils/helpers';

const LogSheet = () => {
  const [alarmHistories, setAlarmHistories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [submittingIds, setSubmittingIds] = useState(new Set());
  const [remarks, setRemarks] = useState({});

  // Get alarm settings from localStorage
  const alarmSettings = JSON.parse(localStorage.getItem('alarmSettings')) || [];

  useEffect(() => {
    fetchAlarmHistories();
  }, []);

  const fetchAlarmHistories = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/AlarmHistory/get-alarm-pending-remarks');
      
      if (response.data && response.data.result && response.data.result.alarmHistoryList) {
        // Sort by resolvedOn (newest first)
        const sortedHistories = response.data.result.alarmHistoryList.sort((a, b) => 
          getApiDateTimeMillis(b.resolvedOn) - getApiDateTimeMillis(a.resolvedOn)
        );
        setAlarmHistories(sortedHistories);
      } else {
        setAlarmHistories([]);
      }
    } catch (error) {
      console.error('Error fetching alarm histories:', error);
      toast.error('Failed to load alarm histories');
      setAlarmHistories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getAlarmName = (masterAlarmId) => {
    const alarmSetting = alarmSettings.find(setting => setting.masterAlarmId === masterAlarmId);
    return alarmSetting ? alarmSetting.alarmName : `Alarm ${masterAlarmId}`;
  };

  const getAlarmColor = (masterAlarmId) => {
    const alarmSetting = alarmSettings.find(setting => setting.masterAlarmId === masterAlarmId);
    return alarmSetting ? alarmSetting.color : '#cccccc';
  };

  const handleRemarkChange = (id, value) => {
    if (value.length <= 100) {
      setRemarks(prev => ({
        ...prev,
        [id]: value
      }));
    }
  };

  const handleSubmit = async (alarmHistoryId) => {
    const remark = remarks[alarmHistoryId]?.trim();
    
    if (!remark) {
      toast.error('Please enter a remark before submitting');
      return;
    }

    setSubmittingIds(prev => new Set(prev).add(alarmHistoryId));

    try {
      const payload = {
        AlarmHistoryId: alarmHistoryId,
        Comment: remark
      };

      const response = await axiosInstance.post('/AlarmManagement/reset-alarm', payload);

      if (response.data && response.data.result) {
        toast.success('Remark submitted successfully');
        
        // Remove the record from the list
        setAlarmHistories(prev => prev.filter(history => history.id !== alarmHistoryId));
        
        // Clear the remark for this record
        setRemarks(prev => {
          const newRemarks = { ...prev };
          delete newRemarks[alarmHistoryId];
          return newRemarks;
        });
      } else {
        toast.error(response.data?.errorResponse?.message || 'Failed to submit remark');
      }
    } catch (error) {
      console.error('Error submitting remark:', error);
      toast.error(error.response?.data?.errorResponse?.message || 'Failed to submit remark');
    } finally {
      setSubmittingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(alarmHistoryId);
        return newSet;
      });
    }
  };

  const formatDateTime = (dateTimeString) => {
    return formatApiDateTime(dateTimeString);
  };

  if (isLoading) {
    return (
      <Flex height="100%" justify="center" align="center">
        <Text size="3">Loading pending logs...</Text>
      </Flex>
    );
  }

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      
      <Flex direction="column" gap="4">
        <Flex justify="between" align="center">
          <Heading size="5">Pending Log Remarks</Heading>
          <Text size="2" color="gray">
            {alarmHistories.length} pending {alarmHistories.length === 1 ? 'log' : 'logs'}
          </Text>
        </Flex>

        {alarmHistories.length === 0 ? (
          <Card size="3">
            <Flex direction="column" align="center" justify="center" py="8">
              <Text size="4" color="gray" weight="medium">
                No pending logs
              </Text>
              <Text size="2" color="gray" mt="2">
                All alarm histories have been completed
              </Text>
            </Flex>
          </Card>
        ) : (
          <Flex direction="column" gap="3">
            {alarmHistories.map((history) => {
              const isSubmitting = submittingIds.has(history.id);
              const currentRemark = remarks[history.id] || '';
              const alarmColor = getAlarmColor(history.masterAlarmId);
              
              return (
                <Card key={history.id} size="3">
                  <Flex direction="column" gap="4">
                    {/* Header with alarm info */}
                    <Flex justify="between" align="start">
                      <Flex direction="column" gap="2">
                        <Flex align="center" gap="3">
                          <div 
                            className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                            style={{ backgroundColor: alarmColor }}
                          />
                          <Text size="4" weight="bold">
                            {getAlarmName(history.masterAlarmId)}
                          </Text>
                        </Flex>
                        
                        <Flex gap="4" align="center">
                          <Flex direction="column">
                            <Text size="2" color="gray">Room</Text>
                            <Text size="3" weight="medium">{history.roomNumber}</Text>
                          </Flex>
                          
                          <Flex direction="column">
                            <Text size="2" color="gray">Bed</Text>
                            <Text size="3" weight="medium">{history.bedNumber}</Text>
                          </Flex>
                          
                          <Flex direction="column">
                            <Text size="2" color="gray">Resolved On</Text>
                            <Text size="3" weight="medium">{formatDateTime(history.resolvedOn)}</Text>
                          </Flex>
                        </Flex>
                      </Flex>            
                    </Flex>

                    {/* Remark input section */}
                    <Box>
                      <Flex direction="column" gap="2">
                        <Flex justify="between" align="center">
                          <Text size="3" weight="medium">
                            Remark <span style={{ color: 'var(--red-9)' }}>*</span>
                          </Text>
                          <Text size="2" color="gray">
                            {currentRemark.length}/100
                          </Text>
                        </Flex>
                        
                        <Flex gap="3" align="end">
                          <Box style={{ flex: 1 }}>
                            <textarea
                              value={currentRemark}
                              onChange={(e) => handleRemarkChange(history.id, e.target.value)}
                              placeholder="Enter your remark for this alarm resolution..."
                              className="w-full p-3 border-2 border-gray-300 rounded-lg resize-none focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
                              style={{ minHeight: '80px' }}
                              rows={3}
                              disabled={isSubmitting}
                            />
                          </Box>
                          
                          <Button
                            onClick={() => handleSubmit(history.id)}
                            disabled={isSubmitting || !currentRemark.trim()}
                            size="3"
                            style={{ 
                              backgroundColor: '#2563eb',
                              opacity: (isSubmitting || !currentRemark.trim()) ? 0.6 : 1,
                              minWidth: '100px'
                            }}
                          >
                            {isSubmitting ? (
                              <Text size="2">Sending...</Text>
                            ) : (
                              <Flex align="center" gap="2">
                                <BiSend />
                                <Text size="2">Submit</Text>
                              </Flex>
                            )}
                          </Button>
                        </Flex>
                      </Flex>
                    </Box>
                  </Flex>
                </Card>
              );
            })}
          </Flex>
        )}
      </Flex>
    </>
  );
};

export default LogSheet;
